"use client";

import { type FormEvent, useEffect, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useAuth } from "@/lib/auth";
import { getAuthReturnTo, saveAuthReturnToFromQuery } from "@/lib/auth-return";
import AuthPageHeader from "@/components/auth/AuthPageHeader";
import GoogleAuthButton from "@/components/auth/GoogleAuthButton";
import AuthDivider from "@/components/auth/AuthDivider";

export default function SignupPage() {
  const { signInWithGoogle, signUpWithEmail } = useAuth();
  const router = useRouter();
  const [error, setError] = useState<string | null>(null);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [isEmailLoading, setIsEmailLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");

  // 大学からの招待メールなど、外部の導線が `next` で復帰先を指定してくることがある。
  // 既存の復帰先（sessionStorage）へ寄せておき、認証後の遷移は共通の仕組みに任せる。
  useEffect(() => {
    saveAuthReturnToFromQuery();
  }, []);

  const isLoading = isGoogleLoading || isEmailLoading;

  const handleGoogleSignup = async () => {
    setError(null);
    setIsGoogleLoading(true);
    const returnTo = getAuthReturnTo();
    const { error } = await signInWithGoogle({
      redirectTo: returnTo ?? "/onboarding",
    });
    if (error) {
      setError(error);
      setIsGoogleLoading(false);
    }
  };

  const handleEmailSignup = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password !== passwordConfirm) {
      setError("パスワードが一致しません");
      return;
    }

    setIsEmailLoading(true);
    const returnTo = getAuthReturnTo();
    const emailRedirectTo = returnTo
      ? `${window.location.origin}${returnTo}`
      : `${window.location.origin}/onboarding`;
    const { error, needsConfirmation } = await signUpWithEmail(
      email,
      password,
      emailRedirectTo
    );
    if (error) {
      setError(error);
      setIsEmailLoading(false);
      return;
    }
    if (needsConfirmation) {
      setEmailSent(true);
      setIsEmailLoading(false);
      return;
    }
    // authReturnTo がある場合は onAuthStateChange 内で claim → redirect が実行される
    if (!returnTo) {
      router.push("/onboarding");
    }
  };

  if (emailSent) {
    return (
      <div className="min-h-screen bg-canvas flex items-center justify-center px-4">
        <div className="w-full max-w-sm">
          <AuthPageHeader
            title="新規登録"
            subtitle=""
          />
          <div className="text-center space-y-4">
            <div className="w-16 h-16 bg-accent/10 rounded-full flex items-center justify-center mx-auto">
              <span className="text-3xl">✉</span>
            </div>
            <h2 className="text-lg font-semibold text-text">
              確認メールを送信しました
            </h2>
            <p className="text-sm text-gray-500 leading-relaxed">
              ご登録のメールアドレスに確認メールを送信しました。
              <br />
              メール内のリンクをクリックして登録を完了してください。
            </p>
            <Link
              href="/login"
              className="inline-block text-accent hover:text-accent-hover font-medium text-sm mt-4"
            >
              ログインページへ
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-canvas flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <AuthPageHeader
          title="新規登録"
          subtitle="無料アカウントを作成して、面接練習を始めましょう"
        />

        <p className="text-xs text-gray-500 text-center leading-relaxed mb-4">
          アカウントを作成することで、
          <Link href="/agreement" target="_blank" className="text-accent hover:text-accent-hover underline">利用規約</Link>
          と
          <Link href="/privacy" target="_blank" className="text-accent hover:text-accent-hover underline">プライバシーポリシー</Link>
          に同意したものとみなされます。
        </p>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
            {error}
          </div>
        )}

        <GoogleAuthButton
          label="Googleで始める"
          loadingLabel="リダイレクト中..."
          isLoading={isLoading}
          onClick={handleGoogleSignup}
        />

        <AuthDivider />

        <form onSubmit={handleEmailSignup} className="space-y-4">
          <div>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="メールアドレス"
              required
              disabled={isLoading}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
            />
          </div>
          <div>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="パスワード（6文字以上）"
              required
              minLength={6}
              disabled={isLoading}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
            />
          </div>
          <div>
            <input
              type="password"
              value={passwordConfirm}
              onChange={(e) => setPasswordConfirm(e.target.value)}
              placeholder="パスワード（確認）"
              required
              minLength={6}
              disabled={isLoading}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
            />
          </div>
          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-accent hover:bg-accent-hover text-on-accent px-4 py-3 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isEmailLoading ? "登録中..." : "新規登録"}
          </button>
        </form>

        <p className="text-center text-sm text-gray-500 mt-6">
          すでにアカウントをお持ちですか？{" "}
          <Link
            href="/login"
            className="text-accent hover:text-accent-hover font-medium"
          >
            ログイン
          </Link>
        </p>
      </div>
    </div>
  );
}
